import { z } from 'zod';

// ─── member addresses vocabularies ──────────────────────────────────────────
//
// Governed enums for the append-only `member_addresses` history (ADR-130). A
// member's postal addresses are rows, never columns on a member-level table:
// an address a member gives is a fact at a point in time, an order that ships
// to it must keep pointing at the address it shipped to, and a member moving
// house appends the next row rather than rewriting the one their open order
// references. The `as const` arrays drive BOTH the `text`+CHECK constraints
// rendered in the DB schema and any wire schema (ADR-056 Rule 8). The row
// shape itself is NOT mirrored here: readers consume
// `typeof memberAddresses.$inferSelect` (ADR-066 Rules 1 and 3).

/**
 * What the address is for. `home` is the residential address the member
 * confirms in registration — the jurisdiction input for the minor
 * derivation and telehealth licensing. `shipping` is where physical goods
 * (NRT, medication) go when that differs from home; a fulfilment path that
 * finds no `shipping` row falls back to the latest `home` row itself.
 */
export const MEMBER_ADDRESS_KIND = ['home', 'shipping'] as const;
export const memberAddressKindSchema = z.enum(MEMBER_ADDRESS_KIND);
export type MemberAddressKind = z.infer<typeof memberAddressKindSchema>;

/**
 * Which writer appended the row. `registration`: the member confirmed it in
 * the registration flow. `legacy_import`: copied from backend2's
 * `user_addresses` by the member migration, with `source_ref` carrying the
 * 1.0 row id so a re-run is idempotent. `atlas_staff`: a staff edit from the
 * member page. A member self-edit surface lands with its own value.
 */
export const MEMBER_ADDRESS_SOURCE = ['registration', 'legacy_import', 'atlas_staff'] as const;
export const memberAddressSourceSchema = z.enum(MEMBER_ADDRESS_SOURCE);
export type MemberAddressSource = z.infer<typeof memberAddressSourceSchema>;

/**
 * ISO 3166-1 alpha-2 country code. Every address today is US (the
 * registration flow collects a US state and ZIP), so the column defaults to
 * `US`; the pattern is the DB CHECK's source (regexCheck), so a code it
 * rejects can never reach the table.
 */
export const COUNTRY_CODE_PATTERN = /^[A-Z]{2}$/;
export const DEFAULT_COUNTRY_CODE = 'US';

/**
 * Every ISO 3166-1 alpha-2 code (249 as of the 2024 register). The pattern
 * above is the CHECK's grammar; this is the vocabulary a free-form legacy
 * value is admitted against, so `ZZ` — two letters, no country — is coerced
 * rather than stored.
 */
export const ISO_3166_ALPHA2_CODES = [
  'AD',
  'AE',
  'AF',
  'AG',
  'AI',
  'AL',
  'AM',
  'AO',
  'AQ',
  'AR',
  'AS',
  'AT',
  'AU',
  'AW',
  'AX',
  'AZ',
  'BA',
  'BB',
  'BD',
  'BE',
  'BF',
  'BG',
  'BH',
  'BI',
  'BJ',
  'BL',
  'BM',
  'BN',
  'BO',
  'BQ',
  'BR',
  'BS',
  'BT',
  'BV',
  'BW',
  'BY',
  'BZ',
  'CA',
  'CC',
  'CD',
  'CF',
  'CG',
  'CH',
  'CI',
  'CK',
  'CL',
  'CM',
  'CN',
  'CO',
  'CR',
  'CU',
  'CV',
  'CW',
  'CX',
  'CY',
  'CZ',
  'DE',
  'DJ',
  'DK',
  'DM',
  'DO',
  'DZ',
  'EC',
  'EE',
  'EG',
  'EH',
  'ER',
  'ES',
  'ET',
  'FI',
  'FJ',
  'FK',
  'FM',
  'FO',
  'FR',
  'GA',
  'GB',
  'GD',
  'GE',
  'GF',
  'GG',
  'GH',
  'GI',
  'GL',
  'GM',
  'GN',
  'GP',
  'GQ',
  'GR',
  'GS',
  'GT',
  'GU',
  'GW',
  'GY',
  'HK',
  'HM',
  'HN',
  'HR',
  'HT',
  'HU',
  'ID',
  'IE',
  'IL',
  'IM',
  'IN',
  'IO',
  'IQ',
  'IR',
  'IS',
  'IT',
  'JE',
  'JM',
  'JO',
  'JP',
  'KE',
  'KG',
  'KH',
  'KI',
  'KM',
  'KN',
  'KP',
  'KR',
  'KW',
  'KY',
  'KZ',
  'LA',
  'LB',
  'LC',
  'LI',
  'LK',
  'LR',
  'LS',
  'LT',
  'LU',
  'LV',
  'LY',
  'MA',
  'MC',
  'MD',
  'ME',
  'MF',
  'MG',
  'MH',
  'MK',
  'ML',
  'MM',
  'MN',
  'MO',
  'MP',
  'MQ',
  'MR',
  'MS',
  'MT',
  'MU',
  'MV',
  'MW',
  'MX',
  'MY',
  'MZ',
  'NA',
  'NC',
  'NE',
  'NF',
  'NG',
  'NI',
  'NL',
  'NO',
  'NP',
  'NR',
  'NU',
  'NZ',
  'OM',
  'PA',
  'PE',
  'PF',
  'PG',
  'PH',
  'PK',
  'PL',
  'PM',
  'PN',
  'PR',
  'PS',
  'PT',
  'PW',
  'PY',
  'QA',
  'RE',
  'RO',
  'RS',
  'RU',
  'RW',
  'SA',
  'SB',
  'SC',
  'SD',
  'SE',
  'SG',
  'SH',
  'SI',
  'SJ',
  'SK',
  'SL',
  'SM',
  'SN',
  'SO',
  'SR',
  'SS',
  'ST',
  'SV',
  'SX',
  'SY',
  'SZ',
  'TC',
  'TD',
  'TF',
  'TG',
  'TH',
  'TJ',
  'TK',
  'TL',
  'TM',
  'TN',
  'TO',
  'TR',
  'TT',
  'TV',
  'TW',
  'TZ',
  'UA',
  'UG',
  'UM',
  'US',
  'UY',
  'UZ',
  'VA',
  'VC',
  'VE',
  'VG',
  'VI',
  'VN',
  'VU',
  'WF',
  'WS',
  'YE',
  'YT',
  'ZA',
  'ZM',
  'ZW',
] as const;

/**
 * Spellings a person writes for a country that ISO does not: `UK` is the one
 * backend2 actually holds (the code is `GB`). Keys and values are upper-case;
 * a lookup normalises its input first.
 */
export const COUNTRY_CODE_ALIASES: Readonly<Record<string, string>> = { UK: 'GB' };

const ISO_3166_ALPHA2 = new Set<string>(ISO_3166_ALPHA2_CODES);

/**
 * A free-form country value as its ISO 3166-1 alpha-2 code, or null when it
 * is not one: trimmed, upper-cased, alias-resolved, then required to be a
 * code the register lists. The caller decides what a null becomes (the
 * legacy import substitutes `DEFAULT_COUNTRY_CODE` and names the coercion).
 */
export function normaliseCountryCode(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const candidate = raw.trim().toUpperCase();
  const resolved = COUNTRY_CODE_ALIASES[candidate] ?? candidate;
  return ISO_3166_ALPHA2.has(resolved) ? resolved : null;
}
